import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import Button from "@components/buttons/Button";
import DirectionButton from "@components/buttons/DirectionButton";
import styles from "@components/CancelConsult.module.css";
import DateStamp from "@components/dates/DateStamp";
import TimeRange from "@components/dates/TimeRange";
import ExpertAvatar from "@components/ExpertAvatar";
import LargeText from "@components/text/LargeText";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMountedTracking from "@hooks/useMountedTracking";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import {
  ConsultCancelDocument,
  ConsultCancelMutation,
  ConsultCancelMutationVariables,
  GetConsultQuery,
  GetJourneyWithExpertQuery
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  consult: GetConsultQuery["consult"];
  journey: GetJourneyWithExpertQuery["journey"];
  onSuccess: () => void;
};

const CancelConsult = ({ consult, journey, onSuccess }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const history = useHistory();
  const [initialConsultStartTime] = useState(consult.startTime);
  const [cancelConsult] = useMutationWithValidation<ConsultCancelMutation, ConsultCancelMutationVariables>(
    ConsultCancelDocument,
    "consult"
  );
  const isMounted = useMountedTracking();

  useEffect(() => {
    if (initialConsultStartTime !== consult.startTime) {
      history.push(
        journeyRoute({
          journeyId: journey.id,
          linkAfterJourney: "/expert",
          myClient
        })
      );
    }
  }, [consult.startTime, history, initialConsultStartTime, journey.id, myClient]);

  const handleCancelConsult = useCallback(() => {
    setSubmitting(true);
    cancelConsult({
      id: consult.id
    }).then(() => {
      if (isMounted.current) {
        onSuccess();
        setSubmitting(false);
      }
    });
  }, [consult.id, cancelConsult, isMounted, onSuccess]);

  return (
    <>
      <div>
        <DirectionButton direction="back" />
      </div>
      <IonGrid className={styles.bookTimeContent}>
        {consult.expert?.photoUrl && (
          <IonRow>
            <IonCol className={styles.centeredColumn}>
              <ExpertAvatar expert={consult.expert} size={200} />
            </IonCol>
          </IonRow>
        )}
        <IonRow>
          <IonCol className={styles.centeredColumn}>
            <Heading2 data-test="cancel-consult-title">
              <FormattedMessage
                id="pages.consults.cancel.cancelYourConsult"
                values={{ name: consult.expert?.firstName }}
              />
            </Heading2>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol className={styles.centeredColumn}>
            <div>
              <LargeText>
                <DateStamp value={consult.startTime} />
              </LargeText>
            </div>
            <div className={styles.textSpacing}>
              <LargeText>
                <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
              </LargeText>
            </div>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol className={styles.centeredColumn}>
            <div className={`${styles.textSpacing} ion-margin-bottom`}>
              <LargeText>
                <FormattedMessage
                  id="pages.consults.cancel.rescheduleConsult"
                  values={{
                    reschedule: (
                      <InternalLink to={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/expert", myClient })}>
                        {intl.formatMessage({ id: "pages.consults.cancel.reschedule" })}
                      </InternalLink>
                    )
                  }}
                />
              </LargeText>
            </div>

            <Button data-test="cancel-consult" disabled={submitting} onClick={handleCancelConsult}>
              <FormattedMessage id="pages.consults.cancel.title" />
            </Button>
          </IonCol>
        </IonRow>
      </IonGrid>
    </>
  );
};

export default CancelConsult;
